import styled, { css } from "styled-components";

import Footer from "../../components/navigation/Footer";
import {
  mediumScreenAndBelow,
  smallScreenAndAbove,
  xSmallScreen,
} from "../../lib/mediaQuery";
import Accents from "./Accents";

export type BackgroundTypes =
  | "gradient"
  | "gradient-offset"
  | "solid"
  | "blackout"
  | "transparent";

export enum PageTypes {
  NORMAL = `var(--page-max-width)`,
  NARROW = `var(--narrow-page-max-width)`,
  NOPADDING = `none`,
}

export interface PageLayoutProps {
  children?: React.ReactNode;
  className?: string;
  subNavContent?: React.ReactNode;
  subNavBackgroundImage?: string;
  backgroundType?: BackgroundTypes;
  showAccents?: Boolean;
  hideFooter?: Boolean;
  header?: React.ReactNode;
  pageType?: PageTypes;
}

export const PageLayout = ({
  backgroundType = "gradient",
  children,
  className,
  subNavContent,
  subNavBackgroundImage,
  showAccents = false,
  hideFooter = false,
  header = null,
  pageType = PageTypes.NORMAL,
}: PageLayoutProps) => (
  <>
    {showAccents && <Accents />}
    <PageContainer
      pageType={pageType}
      className={className}
      backgroundType={backgroundType}
      hideFooter={hideFooter}
    >
      {subNavContent && (
        <SubNavigation backgroundImage={subNavBackgroundImage}>
          <SubNavigationContent>{subNavContent}</SubNavigationContent>
        </SubNavigation>
      )}
      {header}
      <Content pageType={pageType}>{children}</Content>
      {!hideFooter && <Footer />}
    </PageContainer>
  </>
);

const SubNavigation = styled.div<{ backgroundImage?: string }>`
  z-index: var(--depth-high);
  position: relative;
  width: 100%;
  background: ${(props) =>
    props.backgroundImage &&
    css`
      linear-gradient(0deg, rgba(0, 0, 0, 0.85), rgba(0, 0, 0, 0.65)),
      url(${props.backgroundImage})
    `};
  background-size: cover;
  background-position: center;
`;

const SubNavigationContent = styled.div`
  display: flex;
  flex: 0;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  max-width: var(--navigation-max-width);
  min-height: var(--subnav-height);
  padding: 0 var(--navigation-padding);
  margin: 0 auto;
`;

const Content = styled.main<{ pageType?: PageTypes }>`
  margin: 0 auto;
  width: 100%;
  max-width: ${(p) => p.pageType};
  flex: 1;
  ${(p) =>
    smallScreenAndAbove(css`
      padding: ${p.pageType === PageTypes.NARROW
        ? `100px`
        : p.pageType === PageTypes.NOPADDING
        ? `0`
        : `var(--page-vertical-padding-narrow)
        var(--page-side-padding-narrow) var(--page-vertical-padding-narrow)
        var(--page-side-padding-narrow)`};
    `)};
  ${(p) =>
    xSmallScreen(css`
      padding: ${p.pageType === PageTypes.NORMAL
        ? `var(--spacing-md) var(--spacing-xs)`
        : p.pageType === PageTypes.NOPADDING
        ? `0`
        : `var(--page-vertical-padding-narrow) var(--page-side-padding-narrow)`};
    `)};
`;

const PageContainer = styled.div<PageLayoutProps>`
  flex: 1;
  position: relative;
  display: flex;
  flex-direction: column;
  padding-bottom: var(--footer-height);

  z-index: var(--depth-page);
  background: ${(p: PageLayoutProps) => {
    switch (p.backgroundType) {
      case "gradient":
        return "var(--background-gradient)";
      case "gradient-offset":
        return "var(--background-gradient-offset)";
      case "solid":
        return "var(--background-color)";
      case "blackout":
        return "var(--navigation-background-color)";
      case "transparent":
        return "transparent";
      default:
        return "var(--background-image)";
    }
  }};
  ${(p) =>
    p.pageType === PageTypes.NORMAL &&
    css`
      transition: margin-top var(--transition-speed-slow) var(--easing-standard);
      margin-top: var(--navigation-height);
    `}
`;

export const PageContent = styled.div`
  padding: 0 var(--spacing-md);
  max-width: 1440px;
  width: 100%;
  ${mediumScreenAndBelow(
    css`
      padding: 0 var(--spacing-xs);
    `
  )};
`;
